import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, Eye, EyeOff, CheckCircle, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const { register, error, user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isUpgrade = searchParams.get('upgrade') === 'demo';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    organization_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  // Pre-fill from logged-in user if upgrading
  useEffect(() => {
    if (isUpgrade && user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
      }));
      setAgreedToPrivacy(true); // Already agreed during demo signup
    }
  }, [isUpgrade, user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpgrade = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.organization_name.trim()) {
      setFormError('Please enter your organization name');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('pulse_token');
      const res = await fetch(`${API}/api/auth/upgrade-from-demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ organization_name: formData.organization_name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upgrade failed');
      localStorage.setItem('pulse_token', data.access_token);
      localStorage.setItem('pulse_user', JSON.stringify(data.user));
      setUser(data.user);
      navigate('/dashboard');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (!agreedToPrivacy) {
      setFormError('You must agree to the Privacy Policy to continue');
      return;
    }

    setLoading(true);
    const result = await register(formData);
    
    if (result.success) {
      // Redirect to verification page with email
      navigate(`/verify-account?email=${encodeURIComponent(result.email)}`);
    } else {
      setFormError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-6">
      <div className="mx-auto w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/pulse-logo-with-name.png" 
              alt="Pulse by ProjectAI" 
              className="h-24 object-contain"
            />
          </Link>
        </div>

        <Card className="shadow-lg border-slate-200">
          {isUpgrade && user ? (
            <>
              <CardHeader className="space-y-1 text-center">
                <div className="flex justify-center mb-2">
                  <div className="h-10 w-10 rounded-full bg-[#5AA2AE]/10 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-[#5AA2AE]" />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Ready to use Pulse on your project?
                </CardTitle>
                <CardDescription>
                  Set up your organization to get started with a 30-day free trial
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpgrade} className="space-y-4">
                  {formError && (
                    <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                      {formError}
                    </div>
                  )}

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-500">Signed in as</p>
                    <p className="text-sm font-medium text-slate-900">{user.first_name} {user.last_name}</p>
                    <p className="text-sm text-slate-600">{user.email}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization_name">Organization name</Label>
                    <Input
                      id="organization_name"
                      name="organization_name"
                      placeholder="Acme Corp"
                      value={formData.organization_name}
                      onChange={handleChange}
                      required
                      autoFocus
                      data-testid="upgrade-org-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    disabled={loading}
                    data-testid="upgrade-submit-btn"
                  >
                    {loading ? 'Setting up...' : 'Start 30-Day Free Trial'}
                  </Button>
                </form>

                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-3">Your trial includes:</p>
                  <ul className="space-y-2">
                    {['Unlimited projects', 'Full analytics', 'AI-powered insights'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </>
          ) : (
          <>
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Start your free trial
            </CardTitle>
            <CardDescription>
              30 days free, no credit card required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(formError || error) && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                  {formError || error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    data-testid="register-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    placeholder="Doe"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization name</Label>
                <Input
                  id="organization_name"
                  name="organization_name"
                  placeholder="Acme Corp"
                  value={formData.organization_name}
                  onChange={handleChange}
                  required
                  data-testid="register-org-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Must be at least 6 characters</p>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="privacy-policy"
                  checked={agreedToPrivacy}
                  onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                  className="rounded border-slate-300 mt-0.5"
                  data-testid="privacy-policy-checkbox"
                />
                <label htmlFor="privacy-policy" className="text-sm text-slate-600 leading-tight">
                  I agree to the{' '}
                  <a
                    href="https://projectai.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#5AA2AE] hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                disabled={loading || !agreedToPrivacy}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-400">or</span></div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/projects';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-register-btn"
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign up with Google
            </Button>

            {/* Trial benefits */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-3">Your trial includes:</p>
              <ul className="space-y-2">
                {['Unlimited projects', 'Full analytics', 'AI-powered insights'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-[#5AA2AE] hover:underline">
                Sign in
              </Link>
            </div>
          </CardContent>
          </>
          )}
        </Card>
      </div>
    </div>
  );
}
